import axios from "axios";

import { API_ENDPOINTS } from "./endpoints";

const SESSION_KEY = "symmetron_auth_session";
const AUTH_CHANGED_EVENT = "symmetron-auth-changed";
const PUBLIC_API_PATHS = new Set([
    API_ENDPOINTS.auth.login,
    API_ENDPOINTS.admin.login
]);

const notifyAuthChanged = () => window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));

export const getSession = () => {
    try {
        const session = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");

        if (!session?.token) {
            return null;
        }

        if (session.expiresAt && Date.parse(session.expiresAt) <= Date.now()) {
            localStorage.removeItem(SESSION_KEY);
            return null;
        }

        return session;
    } catch {
        localStorage.removeItem(SESSION_KEY);
        return null;
    }
};

export const getToken = () => getSession()?.token || "";
export const hasToken = () => Boolean(getToken());
export const hasRole = (role) => getSession()?.roles?.includes(role) || false;
export const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const saveSession = (data) => {
    const token = data?.token;

    if (!token) {
        throw new Error("Сервер не вернул токен доступа");
    }

    const session = {
        token,
        expiresAt: data.expires_at || null,
        employeeId: data.employee_id,
        employeeName: data.employee_name || "",
        avatar: data.avatar || null,
        roles: Array.isArray(data.roles) ? data.roles : []
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    notifyAuthChanged();
    return session;
};

export const clearSession = () => {
    localStorage.removeItem(SESSION_KEY);
    notifyAuthChanged();
};

const login = async (url, email, password) => {
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password })
    });

    if (!response.ok) {
        throw new Error(response.status === 401
            ? "Неверный email, пароль или недостаточно прав"
            : `Ошибка входа: ${response.status}`);
    }

    return saveSession(await response.json());
};

export const loginEmployee = (email, password) => login(API_ENDPOINTS.auth.login, email, password);
export const loginAdmin = (email, password) => login(API_ENDPOINTS.admin.login, email, password);

export const fetchProfile = async () => {
    const response = await fetch(API_ENDPOINTS.auth.profile);
    if (!response.ok) throw new Error(`Ошибка профиля: ${response.status}`);
    return response.json();
};

export const saveAvatar = async avatar => {
    const response = await fetch("/api/profile/avatar", {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ avatar })
    });
    if (!response.ok) throw new Error("Не удалось сохранить аватарку");
    const result = await response.json();
    const session = getSession();
    if (session) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, avatar: result.avatar }));
        notifyAuthChanged();
    }
    return result;
};

export const changeOwnPassword = async (currentPassword, newPassword) => {
    const response = await fetch(API_ENDPOINTS.auth.password, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword
        })
    });

    if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || `Ошибка смены пароля: ${response.status}`);
    }

    clearSession();
};

export const logout = async () => {
    try {
        await fetch(API_ENDPOINTS.auth.logout, { method: "POST" });
    } finally {
        clearSession();
    }
};

const isProtectedApiUrl = (url) => {
    const value = typeof url === "string" ? url : url?.url;
    if (!value) return false;

    const parsed = new URL(value, window.location.origin);
    return parsed.origin === window.location.origin
        && parsed.pathname.startsWith("/api/")
        && !PUBLIC_API_PATHS.has(parsed.pathname);
};

let interceptorsInstalled = false;

export const installAuthInterceptors = () => {
    if (interceptorsInstalled) return;
    interceptorsInstalled = true;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
        if (!isProtectedApiUrl(input)) {
            return nativeFetch(input, init);
        }

        const headers = new Headers(input instanceof Request ? input.headers : undefined);
        new Headers(init.headers || {}).forEach((value, key) => headers.set(key, value));

        const token = getToken();
        if (token) headers.set("Authorization", `Bearer ${token}`);

        return nativeFetch(input, { ...init, headers }).then(response => {
            if (response.status === 401 && getToken()) clearSession();
            return response;
        });
    };

    axios.interceptors.request.use(config => {
        if (isProtectedApiUrl(config.url)) {
            config.headers = config.headers || {};
            const token = getToken();
            if (token) config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        response => response,
        error => {
            if (error?.response?.status === 401 && getToken()) clearSession();
            return Promise.reject(error);
        }
    );
};

export const subscribeToAuth = (listener) => {
    window.addEventListener(AUTH_CHANGED_EVENT, listener);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, listener);
};
